import { RateSource } from "@redotech/money/rate-source";
import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  getMarginBreakdown,
  MarginBreakdown,
} from "@redotech/redo-fulfillment-operations-common/shipping/margin-breakdown";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { TextAndSupportingText } from "@redotech/redo-web/text-and-supporting-text";
import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLabelText } from "../../common/shipment-rate-selection-dropdown";
import {
  getDeliveryText,
  getEstimatedDeliveryText,
} from "../../common/shipment-rate-text";
import { OutboundRateOption } from "../../common/util";

/** Wide enough for a five-figure amount without wrapping. */
const AMOUNT_COLUMN = "7rem";

interface Amount {
  amount: number;
  currency: string;
}

/** Don't sign-color a figure we're refusing to show. */
function marginTextColor(margin: number, mixedCurrency: boolean) {
  if (mixedCurrency) {
    return "secondary" as const;
  }
  return margin < 0 ? ("error" as const) : ("success" as const);
}

export const MarginBreakdownModal = memo(function MarginBreakdownModal({
  open,
  onClose,
  fulfillmentGroup,
  selectedRate,
  requestedShippingService,
}: {
  open: boolean;
  onClose: () => void;
  fulfillmentGroup: FulfillmentGroupData;
  selectedRate: OutboundRateOption;
  requestedShippingService: string;
}) {
  const team = useRequiredContext(TeamContext);

  const navigate = useNavigate();

  const breakdown = useMemo(
    () =>
      getMarginBreakdown(
        fulfillmentGroup,
        selectedRate.rate || 0,
        selectedRate.landedCost,
      ),
    [fulfillmentGroup, selectedRate],
  );

  const customerCurrency = fulfillmentGroup.totalShippingCost.currency;
  const merchantCurrency = selectedRate.currency ?? customerCurrency;
  const estimatedDeliveryText = getEstimatedDeliveryText(selectedRate);

  /**
   * Margin nets the two columns against each other, so it only means something
   * when the rate and its quoted fees share the revenue currency.
   */
  const mixedCurrency =
    merchantCurrency !== customerCurrency ||
    (!!breakdown.merchantInternationalFees &&
      breakdown.merchantInternationalFees.currency !== customerCurrency);

  return (
    <RedoModal
      buttonPlacement="tight"
      isOpen={open}
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Improve margin",
        size: "sm",
        onClick: () => {
          navigate(`/stores/${team._id}/conversion/checkouts`);
        },
      }}
      subtitle="See what you're paying vs. what customers are paying in shipping methods."
      title="Margin breakdown"
    >
      <Flex align="center" dir="row" gap="md" pb="lg">
        <ShippingLogo carrier={selectedRate.carrier} size={LogoSize.MEDIUM} />
        <Tooltip title={estimatedDeliveryText}>
          <Flex>
            <TextAndSupportingText
              mainTextSize="sm"
              supportingText={serviceSubtitle(
                requestedShippingService,
                selectedRate,
              )}
              supportingTextSize="xs"
              text={getLabelText(selectedRate)}
            />
          </Flex>
        </Tooltip>
      </Flex>
      <Flex dir="column" gap="none">
        <HeaderRow />
        <ShippingRows
          breakdown={breakdown}
          customerCurrency={customerCurrency}
          merchantCurrency={merchantCurrency}
          rate={selectedRate.rate ?? 0}
        />
        <LandedCostRows
          breakdown={breakdown}
          customerCurrency={customerCurrency}
        />
        <Row
          customer={{
            amount: breakdown.customerTotal,
            currency: customerCurrency,
          }}
          emphasis
          label="Total"
          merchant={{
            amount: breakdown.merchantTotal,
            currency: merchantCurrency,
          }}
        />
        <BreakdownLine borderless>
          <RowLabel emphasis label="Margin" />
          <Flex basis={AMOUNT_COLUMN} shrink={0} />
          <Flex basis={AMOUNT_COLUMN} dir="row" justify="flex-end" shrink={0}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textAlign="right"
              textColor={marginTextColor(breakdown.totalMargin, mixedCurrency)}
              whiteSpace="nowrap"
            >
              {mixedCurrency ? (
                "—"
              ) : (
                <MoneyText
                  source={RateSource.Stripe}
                  value={toMoney({
                    amount: breakdown.totalMargin,
                    currency: customerCurrency,
                  })}
                />
              )}
            </Text>
          </Flex>
        </BreakdownLine>
      </Flex>
    </RedoModal>
  );
});

/** One row of the comparison: label, then the two aligned amount columns. */
const BreakdownLine = memo(function BreakdownLine({
  children,
  indented = false,
  borderless = false,
  align = "center",
}: {
  children: React.ReactNode;
  indented?: boolean;
  borderless?: boolean;
  align?: "center" | "flex-end";
}) {
  return (
    <Flex
      align={align}
      borderBottomWidth={borderless ? "none" : "1px"}
      borderColor="primary"
      borderStyle="solid"
      dir="row"
      gap="lg"
      pl={indented ? "xl" : undefined}
      py="lg"
    >
      {children}
    </Flex>
  );
});

const RowLabel = memo(function RowLabel({
  label,
  emphasis = false,
}: {
  label: string;
  emphasis?: boolean;
}) {
  return (
    <Flex dir="row" grow={1} minW="none">
      <Text
        fontSize={emphasis ? "xs" : "xxs"}
        fontWeight={emphasis ? "semibold" : "regular"}
        textColor="primary"
      >
        {label}
      </Text>
    </Flex>
  );
});

const HeaderRow = memo(function HeaderRow() {
  return (
    <BreakdownLine align="flex-end">
      <Flex dir="row" grow={1} minW="none">
        <HeaderText title="Charge" />
      </Flex>
      <Flex basis={AMOUNT_COLUMN} dir="row" justify="flex-end" shrink={0}>
        <HeaderText title="Customer paid" />
      </Flex>
      <Flex basis={AMOUNT_COLUMN} dir="row" justify="flex-end" shrink={0}>
        <HeaderText title="You pay" />
      </Flex>
    </BreakdownLine>
  );
});

const HeaderText = memo(function HeaderText({ title }: { title: string }) {
  return (
    <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
      {title}
    </Text>
  );
});

const ShippingRows = memo(function ShippingRows({
  breakdown,
  rate,
  customerCurrency,
  merchantCurrency,
}: {
  breakdown: MarginBreakdown;
  rate: number;
  customerCurrency: string;
  merchantCurrency: string;
}) {
  const { customerShipping: shipping, customerInternationalFees } = breakdown;

  const markupLabel =
    shipping.percentageMarkup === null
      ? "Percentage markup"
      : `Percentage markup (${shipping.percentageMarkup}%)`;

  /** Nets landed cost out of customerTotal so Shipping + Landed cost = Total. */
  const customerShippingOnly =
    breakdown.customerTotal - (customerInternationalFees?.total ?? 0);

  return (
    <>
      <Row
        customer={{ amount: customerShippingOnly, currency: customerCurrency }}
        emphasis
        label="Shipping"
        merchant={{ amount: rate, currency: merchantCurrency }}
      />
      <Row
        customer={{
          amount: shipping.labelQuotedCost,
          currency: customerCurrency,
        }}
        indented
        label="Quoted label cost"
      />
      <Row
        customer={{
          amount: shipping.percentageMarkupCost,
          currency: customerCurrency,
        }}
        indented
        label={markupLabel}
      />
      <Row
        customer={{ amount: shipping.fixedMarkup, currency: customerCurrency }}
        indented
        label="Fixed markup"
      />
    </>
  );
});

const LandedCostRows = memo(function LandedCostRows({
  breakdown,
  customerCurrency,
}: {
  breakdown: MarginBreakdown;
  customerCurrency: string;
}) {
  const { customerInternationalFees: theirs, merchantInternationalFees: ours } =
    breakdown;

  if (!theirs && !ours) {
    return null;
  }

  const oursCurrency = ours?.currency ?? customerCurrency;
  const amount = (value: number | undefined, currency: string) =>
    value === undefined ? undefined : { amount: value, currency };

  return (
    <>
      <Row
        customer={amount(theirs?.total, customerCurrency)}
        emphasis
        label="Landed cost"
        merchant={amount(ours?.total, oursCurrency)}
      />
      <Row
        customer={amount(theirs?.dutiesAndFees, customerCurrency)}
        indented
        label="Duties and fees"
        merchant={amount(ours?.dutiesAndFees, oursCurrency)}
      />
      <Row
        customer={amount(theirs?.taxes, customerCurrency)}
        indented
        label="Taxes"
        merchant={amount(ours?.taxes, oursCurrency)}
      />
    </>
  );
});

const Row = memo(function Row({
  label,
  customer,
  merchant,
  indented = false,
  emphasis = false,
}: {
  label: string;
  customer?: Amount;
  merchant?: Amount;
  indented?: boolean;
  emphasis?: boolean;
}) {
  return (
    <BreakdownLine indented={indented}>
      <RowLabel emphasis={emphasis} label={label} />
      <AmountCell emphasis={emphasis} money={customer} />
      <AmountCell emphasis={emphasis} money={merchant} />
    </BreakdownLine>
  );
});

const AmountCell = memo(function AmountCell({
  money,
  emphasis = false,
}: {
  money?: Amount;
  emphasis?: boolean;
}) {
  return (
    <Flex basis={AMOUNT_COLUMN} dir="row" justify="flex-end" shrink={0}>
      <Text
        fontSize={emphasis ? "xs" : "xxs"}
        fontWeight={emphasis ? "medium" : "regular"}
        textAlign="right"
        textColor={money ? "primary" : "secondary"}
        whiteSpace="nowrap"
      >
        {money ? (
          <MoneyText source={RateSource.Stripe} value={toMoney(money)} />
        ) : (
          "—"
        )}
      </Text>
    </Flex>
  );
});

/** What the customer picked at checkout, plus the carrier's estimate. */
function serviceSubtitle(
  requestedShippingService: string,
  selectedRate: OutboundRateOption,
): string {
  const delivery = getDeliveryText(selectedRate);
  return [requestedShippingService, delivery].filter(Boolean).join(" · ");
}
